import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { PurchaseRequest, Variant } from '@teo/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Panel } from '../components/Panel';
import { LocationSelect, variantLabel } from '../components/Operation';
import { PurchaseTable, ReceivePurchaseModal } from '../ops/Purchases';
import { useLoad, useSubmit } from '../lib/useAsync';
import { useToast } from '../lib/useToast';
import { releaseKey, requestKey } from '../lib/idempotency';

/** Vista "Solicitar compra" del local: pide una referencia existente o propone un producto nuevo. */
export function PurchaseRequests() {
  const { t } = useTranslation();
  const { user, can } = useAuth();
  const { show, showError } = useToast();
  const { busy, run } = useSubmit();
  const places = user?.locations ?? [];
  const [selected, setSelected] = useState<number | null>(null);
  const locationId = selected ?? places[0]?.id ?? null;

  const [kind, setKind] = useState<'EXISTING' | 'NEW_PRODUCT'>('EXISTING');
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<Variant[] | null>(null);
  const [variant, setVariant] = useState<Variant | null>(null);
  const [proposed, setProposed] = useState({ name: '', brand: '', category: '', origin: '', line: '' });
  const [qty, setQty] = useState('');
  const [cost, setCost] = useState('');
  const [note, setNote] = useState('');
  const [receiving, setReceiving] = useState<PurchaseRequest | null>(null);

  const requests = useLoad(() => (locationId ? api.purchaseRequests({ location_id: locationId, limit: 100 }) : Promise.resolve(null)), [locationId]);

  const search = (value: string) => {
    setTerm(value);
    if (value.trim().length < 2) return setResults(null);
    api.variants(value.trim()).then(setResults).catch(showError);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!locationId) return;
    void run(async () => {
      try {
        const scope = `compra:solicitud:${locationId}`;
        const body = kind === 'EXISTING'
          ? { location_id: locationId, variant_id: variant?.id, qty: Number(qty), estimated_cost: Number(cost) || 0, note }
          : { location_id: locationId, proposed, qty: Number(qty), estimated_cost: Number(cost) || 0, note };
        const r = await api.createPurchaseRequest(requestKey(scope), body);
        releaseKey(scope);
        setVariant(null); setTerm(''); setResults(null); setQty(''); setCost(''); setNote('');
        setProposed({ name: '', brand: '', category: '', origin: '', line: '' });
        show(t('purchases.sent', { doc: r.doc_no }));
        await requests.reload();
      } catch (err) { showError(err); }
    });
  };

  const ready = Number(qty) > 0 && (kind === 'EXISTING' ? !!variant : proposed.name.trim().length > 0);

  return (
    <>
      <LocationSelect id="prLocation" locations={places} value={locationId} onChange={setSelected} />
      <Panel title={t('purchases.formTitle')} description={t('purchases.formDesc')} badge={null}>
        <form className="formgrid" id="purchaseForm" onSubmit={submit}>
          <div className="field span2">
            <label htmlFor="prType">{t('purchases.type')}</label>
            <select id="prType" value={kind} onChange={(e) => setKind(e.target.value as 'EXISTING' | 'NEW_PRODUCT')}>
              <option value="EXISTING">{t('purchases.typeExisting')}</option>
              <option value="NEW_PRODUCT">{t('purchases.typeNew')}</option>
            </select>
          </div>
          {kind === 'EXISTING' ? (
            <div className="field span2">
              <label htmlFor="prSearch">{t('purchases.search')}</label>
              <input id="prSearch" value={term} autoComplete="off" onChange={(e) => search(e.target.value)} />
              <div className="results" id="prResults">
                {results?.slice(0, 20).map((v) => (
                  <div className="result" key={v.id} data-variant={v.id}>
                    <div className="resulttext">
                      <b>{t('ops.refName', { ref: v.ref, name: v.product_name })}</b>
                      <small>{variantLabel(v)}</small>
                    </div>
                    <button type="button" onClick={() => { setVariant(v); setResults(null); setTerm(''); }}>{t('common.add')}</button>
                  </div>
                ))}
              </div>
              <div className="help" id="prSelected">
                {variant ? `${t('purchases.selected')}: ${variant.ref} · ${variant.product_name}` : t('purchases.pickFirst')}
              </div>
            </div>
          ) : (
            <>
              <div className="field">
                <label htmlFor="prName">{t('purchases.proposedName')}</label>
                <input id="prName" required value={proposed.name} onChange={(e) => setProposed({ ...proposed, name: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="prBrand">{t('purchases.brand')}</label>
                <input id="prBrand" value={proposed.brand} onChange={(e) => setProposed({ ...proposed, brand: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="prCategory">{t('purchases.category')}</label>
                <input id="prCategory" value={proposed.category} onChange={(e) => setProposed({ ...proposed, category: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="prOrigin">{t('purchases.origin')}</label>
                <input id="prOrigin" value={proposed.origin} onChange={(e) => setProposed({ ...proposed, origin: e.target.value })} />
              </div>
              <div className="help span2">{t('purchases.newHelp')}</div>
            </>
          )}
          <div className="field">
            <label htmlFor="prQty">{t('purchases.qty')}</label>
            <input id="prQty" type="number" min={1} required value={qty} onChange={(e) => setQty(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="prCost">{t('purchases.estimatedCost')}</label>
            <input id="prCost" type="number" min={0} value={cost} onChange={(e) => setCost(e.target.value)} />
          </div>
          <div className="field span2">
            <label htmlFor="prNote">{t('purchases.note')}</label>
            <input id="prNote" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <button className="btn black span2" type="submit" id="prSubmit" disabled={busy || !ready}>
            {t('purchases.submit')}
          </button>
        </form>
      </Panel>

      <Panel title={t('purchases.mineTitle')} description={t('purchases.mineDesc')} badge={null}>
        <PurchaseTable
          rows={requests.data?.items ?? []}
          loading={requests.loading && !requests.data}
          showLocation={places.length > 1}
          actions={(r) =>
            r.status === 'AUTHORIZED' && can('purchase.receive') ? (
              <button type="button" className="btn sm black" data-receive={r.doc_no} onClick={() => setReceiving(r)}>
                {t('purchases.receive')}
              </button>
            ) : null
          }
        />
      </Panel>

      <ReceivePurchaseModal target={receiving} onClose={() => setReceiving(null)} onDone={() => void requests.reload()} />
    </>
  );
}
